import { loadStripe, Stripe } from '@stripe/stripe-js';
import { getStripePublishableKey } from '@/lib/env-utils';
import { logger } from '@/lib/logger';

// Global Stripe instance cache
let stripeInstance: Promise<Stripe | null> | null = null;
let isPreloading = false;

/**
 * Preloads the Stripe.js library and caches the instance
 * This should be called early in the application lifecycle
 * to reduce latency when payment forms are needed
 */
export async function preloadStripe(): Promise<void> {
  if (stripeInstance || isPreloading) {
    return;
  }

  isPreloading = true;
  
  try {
    const publishableKey = getStripePublishableKey();
    
    logger.debug('Preloading Stripe.js', {
      keyPrefix: publishableKey.substring(0, 10),
      isLiveKey: publishableKey.startsWith('pk_live'),
    });

    // Create and cache the Stripe instance
    stripeInstance = loadStripe(publishableKey);
    
    // Wait for it to load
    await stripeInstance;
    
    logger.debug('Stripe.js preloaded successfully');
  } catch (error) {
    logger.error('Failed to preload Stripe.js:', error);
  } finally {
    isPreloading = false;
  }
}

/**
 * Returns the cached Stripe instance or creates a new one
 * Use this instead of calling loadStripe directly
 */
export function getStripeInstance(): Promise<Stripe | null> {
  if (!stripeInstance) {
    try {
      const publishableKey = getStripePublishableKey();
      stripeInstance = loadStripe(publishableKey);
    } catch (error) {
      logger.error('Failed to create Stripe instance:', error);
      return Promise.resolve(null);
    }
  }
  
  return stripeInstance;
}

/**
 * Adds the Stripe.js script tag to the document head
 * This is a more aggressive preloading strategy that
 * starts downloading the script immediately
 */
export function preloadStripeScript(): void {
  // Check if script already exists
  if (document.querySelector('script[src*="js.stripe.com"]')) {
    return;
  }

  // Create link tag for preloading
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'script';
  link.href = 'https://js.stripe.com/v3/';
  document.head.appendChild(link);
  
  logger.debug('Added Stripe.js preload link tag');
}